
import { useMemo } from "react";

interface Revenue {
  created_at: string;
  amount: number;
  client?: {
    name: string;
  };
}

export const useRevenueCalculations = (revenueData: Revenue[]) => {
  return useMemo(() => {
    // Calculate monthly revenue
    const monthlyRevenue = revenueData.reduce((acc: Record<string, number>, item) => {
      const date = new Date(item.created_at);
      const monthYear = `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
      
      if (!acc[monthYear]) {
        acc[monthYear] = 0;
      }
      
      acc[monthYear] += Number(item.amount);
      return acc;
    }, {});

    // Calculate client revenue
    const clientRevenue = revenueData.reduce((acc: Record<string, number>, item) => {
      const clientName = item.client?.name || 'Unknown';
      
      if (!acc[clientName]) {
        acc[clientName] = 0;
      }
      
      acc[clientName] += Number(item.amount);
      return acc;
    }, {});

    // Format data for charts
    const monthlyRevenueData = Object.entries(monthlyRevenue).map(([month, amount]) => ({
      month,
      amount
    }));

    const clientRevenueData = Object.entries(clientRevenue)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    const totalRevenue = revenueData.reduce((sum, item) => sum + Number(item.amount), 0);
    const averageRevenue = monthlyRevenueData.length ? totalRevenue / monthlyRevenueData.length : 0;

    return {
      monthlyRevenueData,
      clientRevenueData,
      totalRevenue,
      averageRevenue
    };
  }, [revenueData]);
};
